import React, { useEffect, useRef, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Crosshair } from "@/components/Crosshair";
import { Shirt, Plus, Trash2, Upload, X, Save, Loader2, Eye, EyeOff } from "lucide-react";
import { explodeSwagItems } from "@/lib/swag";

const SEL = "w-full bg-input border border-sniper focus:border-olive outline-none px-2.5 py-2 text-foreground font-body text-sm";
const LBL = "block font-mono text-[10px] uppercase tracking-[1.5px] text-secondary-foreground mb-1.5";
const BTN = "font-mono uppercase text-[10px] font-bold px-3 py-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed";
const CATEGORIES = ["Apparel", "Headwear", "Stickers", "Patches", "Accessories", "Other"];
const SIZE_PRESET = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];

const EMPTY = { name: "", description: "", category: "Apparel", price_cents: null, image_url: "", status: "draft", sort_order: 0, stock: null, sizes: [], colors: [], preorder: false };
const fmtPrice = (c) => (c == null ? "Price coming" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const stockLabel = (s) => (s == null ? "Untracked" : s <= 0 ? "Sold out" : `${s} in stock`);

function Editor({ initial, onSaved, onClose }) {
  const isNew = !initial.item_id;
  const [form, setForm] = useState({ ...EMPTY, ...initial, sizes: initial.sizes || [], colors: initial.colors || [], preorder: !!initial.preorder, price_dollars: initial.price_cents != null ? (initial.price_cents / 100).toString() : "", stock_str: initial.stock != null ? String(initial.stock) : "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState("");
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Shared upload → returns the stored relative URL (used by the primary image + each color photo).
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/swag/upload-photo", fd);
    return data.url;
  };

  const upload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploading("primary");
    try {
      set("image_url", await uploadFile(file));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  const toggleSize = (s) => setForm((f) => ({ ...f, sizes: f.sizes.includes(s) ? f.sizes.filter((x) => x !== s) : [...f.sizes, s] }));
  const addColor = () => setForm((f) => ({ ...f, colors: [...f.colors, { name: "", image_url: "" }] }));
  const setColor = (i, key, val) => setForm((f) => ({ ...f, colors: f.colors.map((c, idx) => idx === i ? { ...c, [key]: val } : c) }));
  const removeColor = (i) => setForm((f) => ({ ...f, colors: f.colors.filter((_, idx) => idx !== i) }));
  const uploadColor = async (i, e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploading(`color-${i}`);
    try { setColor(i, "image_url", await uploadFile(file)); toast.success("Color photo uploaded"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setUploading(""); }
  };
  const clearColorImage = (i) => setColor(i, "image_url", "");

  const save = async () => {
    if (!form.name.trim()) { toast.error("Enter an item name."); return; }
    const priceStr = (form.price_dollars || "").trim();
    let price_cents = null;
    if (priceStr !== "") {
      const n = Number(priceStr);
      if (Number.isNaN(n) || n < 0) { toast.error("Price must be a number ≥ 0, or leave blank for 'price coming'."); return; }
      price_cents = Math.round(n * 100);
    }
    const stockStr = (form.stock_str || "").trim();
    let stock = null;
    if (stockStr !== "") {
      const s = Number(stockStr);
      if (!Number.isInteger(s) || s < 0) { toast.error("Stock must be a whole number ≥ 0, or leave blank for untracked."); return; }
      stock = s;
    }
    const colors = (form.colors || []).map((c) => ({ name: (c.name || "").trim(), image_url: c.image_url || "" })).filter((c) => c.name);
    // With colors, the storefront shows one card per color (each color's own photo). Keep the item's
    // top-level image_url in sync with the first color that has a photo so the admin grid thumbnail +
    // storefront fallback always resolve — the per-color photos remain the source of truth.
    const primaryImage = colors.length > 0 ? ((colors.find((c) => c.image_url) || {}).image_url || form.image_url || "") : form.image_url;
    setBusy(true);
    try {
      const body = { name: form.name.trim(), description: form.description, category: form.category, price_cents, image_url: primaryImage, status: form.status, sort_order: Number(form.sort_order) || 0, stock, sizes: form.sizes, colors, preorder: !!form.preorder };
      if (isNew) await api.post("/admin/swag", body);
      else await api.put(`/admin/swag/${initial.item_id}`, body);
      toast.success(isNew ? "Swag item created." : "Swag item saved.");
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (isNew) { onClose(); return; }
    if (!window.confirm("Delete this swag item?")) return;
    setBusy(true);
    try { await api.delete(`/admin/swag/${initial.item_id}`); toast.success("Deleted."); onSaved(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-olive/40 bg-surface p-4 space-y-4" data-testid="swag-editor">
      <div className="flex items-center justify-between">
        <h3 className="font-heading uppercase tracking-tight text-foreground">{isNew ? "New swag item" : `Edit — ${initial.name || "(unnamed)"}`}</h3>
        <button onClick={onClose} data-testid="swag-editor-close" className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className={LBL}>Name <span className="text-[#E8C46A]">*</span></label>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} className={SEL} data-testid="swag-name" />
        </div>
        <div>
          <label className={LBL}>Category</label>
          <select value={form.category} onChange={(e) => set("category", e.target.value)} className={SEL} data-testid="swag-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={LBL}>Price ($) <span className="text-muted-foreground normal-case">— blank = "price coming"</span></label>
          <input value={form.price_dollars} onChange={(e) => set("price_dollars", e.target.value)} inputMode="decimal" placeholder="0.00" className={SEL} data-testid="swag-price" />
        </div>
        <div>
          <label className={LBL}>Sort order <span className="text-muted-foreground normal-case">— lower shows first</span></label>
          <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className={SEL} data-testid="swag-sort" />
        </div>
        <div>
          <label className={LBL}>Stock <span className="text-muted-foreground normal-case">— blank = untracked (always available); 0 = sold out</span></label>
          <input type="number" min="0" step="1" value={form.stock_str} onChange={(e) => set("stock_str", e.target.value)} placeholder="Untracked" className={SEL} data-testid="swag-stock" />
        </div>
      </div>

      <div>
        <label className={LBL}>Description</label>
        <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={SEL} data-testid="swag-description" />
      </div>

      {(form.colors || []).length === 0 ? (
        <div>
          <label className={LBL}>Image</label>
          <div className="flex items-center gap-3">
            <div className="w-24 h-24 border border-sniper bg-obsidian overflow-hidden flex items-center justify-center shrink-0">
              {form.image_url ? <img src={form.image_url} alt="" className="w-full h-full object-contain p-1" data-testid="swag-image-preview" />
                : <Shirt className="w-8 h-8 text-muted-foreground" />}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="swag-image-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading === "primary"} data-testid="swag-image-upload" className={`${BTN} border border-sniper text-foreground hover:border-olive-bright`}>
                {uploading === "primary" ? <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> : <Upload className="w-3 h-3 inline mr-1 -mt-0.5" />} Upload image
              </button>
              {form.image_url && <button onClick={() => set("image_url", "")} data-testid="swag-image-clear" className={`${BTN} border border-blaze/50 text-blaze hover:bg-blaze/10`}>Remove</button>}
            </div>
          </div>
          <p className="mt-2 font-mono text-[10px] text-muted-foreground">Single product image. If this item comes in more than one colorway, add colors below instead — each color gets its own photo + its own storefront card.</p>
        </div>
      ) : (
        <div className="flex items-start gap-2 border border-sniper bg-obsidian/40 px-3 py-2.5" data-testid="swag-image-percolor-note">
          <Shirt className="w-4 h-4 text-olive-bright shrink-0 mt-0.5" />
          <p className="font-mono text-[10px] text-secondary-foreground leading-relaxed">This item has colors, so each colorway uses its <span className="text-foreground">own photo</span> from the <span className="text-foreground">Colors</span> section below and becomes its own card on the storefront. Upload Olive's and Black's photos there. (The primary/thumbnail image is taken from the first color automatically.)</p>
        </div>
      )}

      <div className="border-t border-sniper pt-4 space-y-4">
        {/* PRE-ORDER: badge + "ships when available" + never sold out (stock stays untracked). */}
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="swag-preorder-toggle">
          <input type="checkbox" checked={!!form.preorder} onChange={(e) => set("preorder", e.target.checked)} className="mt-0.5 accent-olive-bright w-4 h-4" data-testid="swag-preorder-checkbox" />
          <span>
            <span className="font-mono text-[11px] uppercase tracking-[1.5px] text-foreground">Pre-order</span>
            <span className="block font-body text-[12px] text-muted-foreground">Shows a PRE-ORDER badge, a "Pre-order" button, and never sells out (leave Stock blank / untracked).</span>
          </span>
        </label>

        <div>
          <label className={LBL}>Sizes <span className="text-muted-foreground normal-case">— buyer picks one at checkout</span></label>
          <div className="flex flex-wrap gap-2" data-testid="swag-sizes">
            {SIZE_PRESET.map((s) => (
              <button key={s} type="button" onClick={() => toggleSize(s)} data-testid={`swag-size-toggle-${s}`}
                className={`font-mono text-[11px] uppercase min-w-[44px] px-2.5 py-1.5 border transition-colors ${form.sizes.includes(s) ? "border-olive-bright bg-olive/20 text-foreground" : "border-sniper text-muted-foreground hover:text-foreground"}`}>{s}</button>
            ))}
          </div>
        </div>

        <div>
          <label className={LBL}>Colors <span className="text-muted-foreground normal-case">— each colorway gets its OWN photo and its own card on /swag</span></label>
          <div className="space-y-3" data-testid="swag-colors">
            {(form.colors || []).map((c, i) => {
              const cname = (c.name || "").trim();
              return (
              <div key={i} className="border border-sniper bg-obsidian/40 p-3" data-testid={`swag-color-row-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="w-16 h-16 shrink-0 border border-sniper bg-obsidian overflow-hidden flex items-center justify-center">
                    {c.image_url ? <img src={c.image_url} alt={cname} className="w-full h-full object-contain p-1" data-testid={`swag-color-preview-${i}`} /> : <Shirt className="w-6 h-6 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <input value={c.name} onChange={(e) => setColor(i, "name", e.target.value)} placeholder="Color name (e.g. Olive)" className={SEL} data-testid={`swag-color-name-${i}`} />
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className={`${BTN} border border-olive/50 text-olive-bright hover:bg-olive/10 cursor-pointer`} data-testid={`swag-color-upload-${i}`}>
                        {uploading === `color-${i}` ? <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> : <Upload className="w-3 h-3 inline mr-1 -mt-0.5" />} Upload {cname ? `${cname} photo` : "photo"}
                        <input type="file" accept="image/*" onChange={(e) => uploadColor(i, e)} className="hidden" />
                      </label>
                      {c.image_url && <button type="button" onClick={() => clearColorImage(i)} data-testid={`swag-color-clear-${i}`} className={`${BTN} border border-blaze/50 text-blaze hover:bg-blaze/10`}>Remove photo</button>}
                      <span className={`font-mono text-[10px] uppercase ${c.image_url ? "text-olive-bright" : "text-muted-foreground"}`} data-testid={`swag-color-status-${i}`}>{c.image_url ? "✓ photo set" : "no photo yet"}</span>
                      <button type="button" onClick={() => removeColor(i)} data-testid={`swag-color-remove-${i}`} title="Remove this color" className="ml-auto text-blaze hover:bg-blaze/10 p-1.5 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
            <button type="button" onClick={addColor} data-testid="swag-color-add" className={`${BTN} border border-sniper text-foreground hover:border-olive-bright`}>
              <Plus className="w-3 h-3 inline mr-1 -mt-0.5" /> Add color
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className={LBL}>Status</label>
        <div className="inline-flex border border-sniper" data-testid="swag-status">
          {["draft", "published"].map((s) => (
            <button key={s} onClick={() => set("status", s)} data-testid={`swag-status-${s}`}
              className={`font-mono text-[10px] uppercase px-3 py-1.5 transition-colors ${form.status === s ? "bg-olive text-white" : "text-muted-foreground hover:text-foreground"}`}>{s}</button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-sniper">
        <button onClick={save} disabled={busy} data-testid="swag-save" className={`${BTN} bg-olive text-white hover:bg-olive-bright`}>
          <Save className="w-3 h-3 inline mr-1 -mt-0.5" /> {isNew ? "Create item" : "Save changes"}
        </button>
        {!isNew && <button onClick={del} disabled={busy} data-testid="swag-delete" className={`${BTN} border border-blaze/50 text-blaze hover:bg-blaze/10 ml-auto`}><Trash2 className="w-3 h-3 inline mr-1 -mt-0.5" /> Delete</button>}
      </div>
    </div>
  );
}


export default function AdminSwag() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, published: 0, draft: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/swag");
      setItems(data.items || []);
      setCounts(data.counts || { all: 0, published: 0, draft: 0 });
    } catch (e) { toast.error("Could not load swag items."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSaved = () => { setEditing(null); load(); };

  const [seeding, setSeeding] = useState(false);
  const hasTee = items.some((it) => it.item_id === "swag_opticswap_tee");
  const seedTee = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/swag/seed-tee");
      const it = data.item || {};
      toast.success(`${data.created ? "Created" : "Refreshed"} OpticSwap Tee — ${it.status}, $${((it.price_cents || 0) / 100).toFixed(2)}, ${(it.colors || []).map((c) => c.name).join(" + ")}, pre-order ${it.preorder ? "on" : "off"}.`);
      await load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Could not create the tee."); }
    finally { setSeeding(false); }
  };

  return (
    <div className="text-foreground" data-testid="admin-swag">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2"><Shirt className="w-7 h-7 text-olive-bright" /> Swag</h2>
          <p className="text-sm font-body text-secondary-foreground mt-2">Manage the OpticSwag catalog. Published items appear in the storefront. Orders are fulfilled in <span className="text-olive-bright">Admin → Orders</span> (tagged SWAG).</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={seedTee} disabled={seeding} data-testid="swag-seed-tee" className={`${BTN} border border-[#d3a24e]/60 text-[#d3a24e] hover:bg-[#d3a24e]/10`}>
            {seeding ? <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> : <Shirt className="w-3 h-3 inline mr-1 -mt-0.5" />} {hasTee ? "Refresh Tee images" : "Create OpticSwap Tee"}
          </button>
          <button onClick={() => setEditing({})} data-testid="swag-new" className={`${BTN} bg-olive text-white hover:bg-olive-bright`}>
            <Plus className="w-3 h-3 inline mr-1 -mt-0.5" /> New item
          </button>
        </div>
      </div>

      <Crosshair className="my-5" />

      <div className="flex items-center gap-4 mb-4 font-mono text-[11px] uppercase text-muted-foreground" data-testid="swag-counts">
        <span data-testid="swag-count-all">{counts.all} total</span>
        <span className="text-[#4E8A1F]" data-testid="swag-count-published">{counts.published} published</span>
        <span className="text-[#d3a24e]" data-testid="swag-count-draft">{counts.draft} draft</span>
      </div>

      {editing && (
        <div className="mb-6">
          <Editor initial={editing.item_id ? editing : { ...EMPTY }} onSaved={onSaved} onClose={() => setEditing(null)} />
        </div>
      )}

      {loading ? (
        <p className="font-mono text-sm uppercase text-muted-foreground" data-testid="swag-loading">Loading…</p>
      ) : items.length === 0 ? (
        <div className="border border-sniper p-8 text-center" data-testid="swag-empty">
          <Shirt className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="font-mono text-sm uppercase text-muted-foreground">No swag items yet. Create the first one.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="swag-grid">
          {explodeSwagItems(items).map((card) => {
            const it = card.item;
            return (
              <button key={card.key} onClick={() => setEditing(it)} data-testid={`swag-item-${card.slug}`}
                className="text-left border border-sniper bg-surface hover:border-olive-bright transition-colors overflow-hidden">
                <div className="aspect-[4/3] bg-obsidian overflow-hidden flex items-center justify-center">
                  {card.image ? <img src={card.image} alt={card.title} className="w-full h-full object-cover" />
                    : <Shirt className="w-10 h-10 text-muted-foreground" />}
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-heading uppercase tracking-tight text-foreground truncate">{card.title}</span>
                    <span className={`shrink-0 font-mono text-[9px] uppercase px-1.5 py-0.5 border inline-flex items-center gap-1 ${it.status === "published" ? "border-[#4E8A1F]/50 text-[#4E8A1F]" : "border-[#d3a24e]/50 text-[#d3a24e]"}`}>
                      {it.status === "published" ? <Eye className="w-2.5 h-2.5" /> : <EyeOff className="w-2.5 h-2.5" />}{it.status}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 font-mono text-[10px] uppercase text-muted-foreground">
                    <span>{it.category || "—"}</span><span>·</span><span className="text-secondary-foreground">{fmtPrice(it.price_cents)}</span>
                    <span>·</span>
                    <span className={it.stock === 0 ? "text-blaze" : "text-secondary-foreground"} data-testid={`swag-stock-${card.slug}`}>{stockLabel(it.stock)}</span>
                  </div>
                  {card.colorName ? (
                    <div className="mt-1.5 font-mono text-[9px] uppercase tracking-[1px] text-olive-bright" data-testid={`swag-variant-${card.slug}`}>
                      Variant · {card.colorName}
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
